import { register } from '!/repository'
import { openWindow, model as router } from '!/router'
import { resetUnlockId } from '$/info-popup/model'
import { model as session } from '$/session'
import { aye, nay } from '@/helpers'
import { logger } from '@/logger'
import { F } from '@mobily/ts-belt'
import { api, type AccountAccessToken } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { model as paymentModel } from '~/features/payment/generic'
import {
  $externalPaymentBaseUrl,
  $selectedContent,
} from '~/features/payment/generic/model'
import type { TryIsPurchased } from '../index.h'
import {
  getExternalPaymentDataByMediaType,
  getExternalPaymentUrlHelper,
} from './helpers'
import { $externalPayment } from './l10n'

export const $isPopupOpened = //
  paymentModel.$externalPaymentPopupIsOpened.map(F.identity)

export const giveItATry = createEvent<TryIsPurchased>()

$selectedContent.on(giveItATry, (_, { mediaType, entity, externalId }) => {
  return {
    mediaType,
    ...getExternalPaymentDataByMediaType({ mediaType, entity, externalId }),
  }
})

const getPaymentAccountAccessTokenFx = attach({
  effect: api.subscriber.getAccountAccessTokenFx,
})
const $isGetAccessTokenLoading = getPaymentAccountAccessTokenFx.pending

export const isPurchasedFx = attach({
  effect: paymentModel.isPurchasedFx,
  mapParams: ({
    entity,
    id,
    tvShowId,
    seasonId,
    mediaType,
    shouldRefresh,
  }: TryIsPurchased) => ({
    // assume that id is either inside of `entity` or `id`, so one is always defined

    id: entity?.id ?? id!,
    tvShowId: entity?.tvShowId ?? tvShowId,
    seasonId: entity?.seasonId ?? seasonId,
    mediaType: entity?.mediaType ?? mediaType,
    shouldRefresh,
  }),
})

export const closePopup = createEvent()

const $externalPaymentAccessToken = createStore<AccountAccessToken | null>(null)
export const proceed = createEvent()
const proceedFx = createEffect((url: string | null) => {
  if (url) {
    const handler = openWindow(url, 'external_shopping_cart')
    if (handler) return handler
  }
  throw new Error('Failed to open window')
})

// TODO: we can add event listener on 'message', and shopping cart can communicate with us, to actually notify about purchase
// https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
// window.addEventListener("message", (event) => {
//   if (event.origin !== "http://example.org:8080") return
//   ...
// }, false)

sample({
  clock: giveItATry,
  target: isPurchasedFx,
})

sample({
  clock: isPurchasedFx.doneData,
  source: giveItATry,
  filter: ({ link, shouldRefresh }, isPurchased) =>
    aye(link) && aye(isPurchased) && nay(shouldRefresh),
  fn: ({ link }) => link!,
  target: router.navigatePush,
})

sample({
  clock: isPurchasedFx.doneData,
  source: giveItATry,
  filter: ({ link, shouldRefresh }, isPurchased) =>
    nay(link) && aye(isPurchased) && aye(shouldRefresh),
  target: router.refresh, // not sure what to do without link, so, just refresh page just in case
})

sample({
  clock: isPurchasedFx.doneData,
  filter: (isPurchased) => nay(isPurchased),
  target: paymentModel.openExternalPaymentPopup,
})

sample({
  clock: closePopup,
  target: paymentModel.closeExternalPaymentPopup,
})

sample({
  clock: paymentModel.openExternalPaymentPopup,
  target: getPaymentAccountAccessTokenFx,
})

sample({
  clock: getPaymentAccountAccessTokenFx.doneData,
  target: $externalPaymentAccessToken,
})
sample({
  clock: getPaymentAccountAccessTokenFx.failData,
  fn: () => null,
  target: $externalPaymentAccessToken,
})

sample({
  clock: proceed,
  source: {
    token: $externalPaymentAccessToken,
    baseUrl: $externalPaymentBaseUrl,
    session: session.$session,
    selectedContent: $selectedContent,
  },
  fn: ({ baseUrl, session, selectedContent, token }) =>
    getExternalPaymentUrlHelper({ baseUrl, session, selectedContent, token }),
  target: proceedFx,
})

sample({
  clock: proceedFx.done,
  target: closePopup,
})

sample({
  clock: proceedFx.failData,
  fn: (error) => ['failed to open shopping cart window', error],
  target: logger.errorFx,
})

sample({
  clock: closePopup,
  target: resetUnlockId,
})

const $externalPaymentButtonTitle = combine(
  $externalPayment,
  getPaymentAccountAccessTokenFx.pending,
  ({ proceedToPurchase }, tokenLoading) =>
    tokenLoading ? '' : proceedToPurchase
)

/*
 * Register stores and events for skin
 */

register($externalPayment, '$payment/external')
register(
  $externalPaymentButtonTitle,
  '$payment/external/externalPaymentButtonTitle'
)
register(giveItATry, 'payment/external/try')
register(closePopup, 'payment/external/closePopup')
register(proceed, 'payment/external/proceed')
register($isPopupOpened, '$payment/external/isPopupOpened')
register($isGetAccessTokenLoading, '$payment/external/isLoading')
